import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminOperatorAccessList } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const OPERATOR_PAGE_SIZE = 30;
const operatorSortOptions = [
  { value: 'updated-desc', label: '更新日が新しい順' },
  { value: 'profile-missing', label: 'プロフィール未設定順' },
  { value: 'email-asc', label: 'メールアドレス昇順' },
  { value: 'email-desc', label: 'メールアドレス降順' },
];

const accessStatusToUi = (isActive) => ({
  label: isActive ? '管理権限あり' : '管理権限停止中',
  tone: isActive ? 'published' : 'draft',
});

const tableStyle = {
  minWidth: '67rem',
};

const indexColumnStyle = {
  width: '4.5rem',
  minWidth: '4.5rem',
};

const accountColumnStyle = {
  minWidth: '18.5rem',
};

const roleColumnStyle = {
  width: '8rem',
  minWidth: '8rem',
};

const statusColumnStyle = {
  width: '11rem',
  minWidth: '11rem',
};

const dateColumnStyle = {
  width: '11rem',
  minWidth: '11rem',
};

const actionColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
};

const sortOperatorRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'profile-missing') {
      if (left.profileMissing === right.profileMissing) {
        return left.accountEmail.localeCompare(right.accountEmail, 'ja');
      }
      return left.profileMissing ? -1 : 1;
    }

    if (sortKey === 'email-asc') {
      return left.accountEmail.localeCompare(right.accountEmail, 'ja');
    }

    if (sortKey === 'email-desc') {
      return right.accountEmail.localeCompare(left.accountEmail, 'ja');
    }

    const leftDate = Date.parse(left.updatedAt.replace(' ', 'T'));
    const rightDate = Date.parse(right.updatedAt.replace(' ', 'T'));
    const safeLeft = Number.isNaN(leftDate) ? 0 : leftDate;
    const safeRight = Number.isNaN(rightDate) ? 0 : rightDate;
    return safeRight - safeLeft;
  });

  return nextRows;
};

const AdminOperatorsListPage = () => {
  const content = yowanPageContent['/admin/operators'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('updated-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadOperatorRows = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const payload = await fetchYowanAdminOperatorAccessList({
          page: currentPage,
          pageSize: OPERATOR_PAGE_SIZE,
          q: searchQuery.trim(),
          roleKey: 'admin',
        });

        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError('管理者を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOperatorRows();

    return () => {
      isMounted = false;
    };
  }, [content, currentPage, searchQuery]);

  if (!content) {
    return null;
  }

  const sortedRows = sortOperatorRows(rows, sortKey);
  const totalPages = Math.max(1, Math.ceil(apiCount / OPERATOR_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * OPERATOR_PAGE_SIZE;
  const visibleRows = sortedRows;

  return (
    <AdminBlogShell activeNavHref="/admin/operators" routeKey="/admin/operators">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${apiCount}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="氏名 / ふりがな / メールアドレス"
        sortOptions={operatorSortOptions}
      />
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table" style={tableStyle}>
          <thead>
            <tr>
              <th scope="col" style={indexColumnStyle}>#</th>
              <th scope="col" style={accountColumnStyle}>氏名 / メールアドレス</th>
              <th scope="col" style={roleColumnStyle}>権限</th>
              <th scope="col" style={statusColumnStyle}>状態</th>
              <th scope="col" style={dateColumnStyle}>作成日</th>
              <th scope="col" style={dateColumnStyle}>更新日</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => {
              const statusUi = accessStatusToUi(row.isActive);

              return (
                <tr key={row.id}>
                  <td style={indexColumnStyle}>{startIndex + index + 1}</td>
                  <td style={accountColumnStyle}>
                    <p className="yowan-admin-resource-table__title">
                      {row.accountName || 'プロフィール未設定'}
                    </p>
                    <p className="yowan-admin-resource-table__subline">{row.accountEmail || '-'}</p>
                  </td>
                  <td style={roleColumnStyle}>{row.roleLabel}</td>
                  <td style={statusColumnStyle}>
                    <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}>
                      {statusUi.label}
                    </span>
                  </td>
                  <td style={dateColumnStyle}>{row.createdAt || '-'}</td>
                  <td style={dateColumnStyle}>{row.updatedAt || '-'}</td>
                  <td style={actionColumnStyle}>
                    <div className="yowan-admin-resource-table__actions">
                      <Link to={appendYowanPreviewQueryIfNeeded(row.profileEditHref, preservedDomainSearch)}>編集</Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && visibleRows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  {loadError || '表示できる管理者がありません。'}
                </td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={7}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminOperatorsListPage;
